/**
 * 笔记 scope_key 生成（需求 §F5）
 *
 * 必须与服务端 `server-go/internal/service/notes.go` 的 ResolveKey 严格对应：
 *   day   → '2026-09-30'
 *   week  → '2026-W40'      ISO-8601 周（周一为首日）
 *   month → '2026-09'
 *   year  → '2026'
 *
 * ⚠️ 周键不能用自然周（跨年周会错位），必须用 ISO 周 —— 见 docs/UI设计.md §6。
 */

const pad = (n: number) => String(n).padStart(2, '0');

/** ISO-8601 周键：YYYY-Www */
export function isoWeekKey(date: string): string {
  const d = new Date(`${date}T00:00:00Z`);
  const day = d.getUTCDay() || 7;            // 周日=7
  d.setUTCDate(d.getUTCDate() + 4 - day);    // 移到本周四（ISO 规则）
  const year = d.getUTCFullYear();
  const jan1 = new Date(Date.UTC(year, 0, 1));
  const week = Math.ceil(((d.getTime() - jan1.getTime()) / 86_400_000 + 1) / 7);
  return `${year}-W${pad(week)}`;
}

/** ISO 周的周一（周键 → 日期区间起点） */
export function isoWeekMonday(weekKey: string): string {
  const m = /^(\d{4})-W(\d{1,2})$/.exec(weekKey);
  if (!m) return '';
  const year = Number(m[1]);
  const week = Number(m[2]);
  const jan4 = new Date(Date.UTC(year, 0, 4));       // 1/4 必属第 1 周
  const jan4Day = jan4.getUTCDay() || 7;
  const week1Monday = new Date(jan4.getTime() - (jan4Day - 1) * 86_400_000);
  return new Date(week1Monday.getTime() + (week - 1) * 7 * 86_400_000)
    .toISOString().slice(0, 10);
}

/** ISO 周的周日（区间终点） */
export function isoWeekSunday(weekKey: string): string {
  const monday = isoWeekMonday(weekKey);
  if (!monday) return '';
  return new Date(new Date(`${monday}T00:00:00Z`).getTime() + 6 * 86_400_000)
    .toISOString().slice(0, 10);
}

export const monthKey = (date: string) => date.slice(0, 7);
export const yearKey = (date: string) => date.slice(0, 4);

export type Scope = 'day' | 'week' | 'month' | 'year';
export type Period = 'D' | 'W' | 'M' | 'Y' | 'MIN1' | 'MIN5';

/** 周期 + 日期 → 笔记 scope（分时映射回 day） */
export function scopeOf(period: Period, date: string): { scope: Scope; scopeKey: string } {
  const base: Exclude<Period, 'MIN1' | 'MIN5'> =
    period === 'MIN1' || period === 'MIN5' ? 'D' : period;
  switch (base) {
    case 'D': return { scope: 'day', scopeKey: date };
    case 'W': return { scope: 'week', scopeKey: isoWeekKey(date) };
    case 'M': return { scope: 'month', scopeKey: monthKey(date) };
    case 'Y': return { scope: 'year', scopeKey: yearKey(date) };
  }
}

/**
 * 周期 + 日期 → 闭区间 [from, to]（YYYY-MM-DD）。
 *
 * 笔记列表与预测都要「按区间取日记录」：点周K柱看当周所有日笔记，
 * 月/年同理。day 的区间就是当天，两端相同。
 *
 * ⚠️ 已知错位：周按 ISO 周（周一~周日）算，而 K 线周柱是**交易周**。
 * 逢长假（如国庆）一个交易周会横跨两个 ISO 周，此时区间会漏掉
 * 假期前的那几个交易日。与 DateScopeFilter 的显示口径一致，
 * 修它要连后端的周键口径一起改，另开一轮。
 */
export function periodRange(period: Period, date: string): { from: string; to: string } {
  const base: Exclude<Period, 'MIN1' | 'MIN5'> =
    period === 'MIN1' || period === 'MIN5' ? 'D' : period;
  switch (base) {
    case 'D':
      return { from: date, to: date };
    case 'W': {
      const k = isoWeekKey(date);
      const a = isoWeekMonday(k), b = isoWeekSunday(k);
      return a && b ? { from: a, to: b } : { from: date, to: date };
    }
    case 'M':
      return { from: `${monthKey(date)}-01`, to: `${monthKey(date)}-31` };
    case 'Y':
      return { from: `${yearKey(date)}-01-01`, to: `${yearKey(date)}-12-31` };
  }
}

/** scope_key → 人类可读标签 */
export function scopeLabel(scope: Scope, scopeKey: string): string {
  switch (scope) {
    case 'day': return scopeKey;
    case 'week': return `${scopeKey}（${isoWeekMonday(scopeKey).slice(5)}~${isoWeekSunday(scopeKey).slice(5)}）`;
    case 'month': return scopeKey;
    case 'year': return `${scopeKey} 年`;
  }
}